import { useId, useRef, useState } from "react";
import { ChevronDown, Menu } from "lucide-react";

type AdminNavigationProps<T extends string> = {
  tabs: readonly T[];
  activeTab: T;
  onSelect: (tab: T) => void;
};

export default function AdminNavigation<T extends string>({
  tabs,
  activeTab,
  onSelect,
}: AdminNavigationProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const navigationId = useId();
  const toggleRef = useRef<HTMLButtonElement>(null);

  return (
    <aside className="shrink-0 rounded-xl border bg-card p-2 md:sticky md:top-6 md:max-h-[calc(100dvh-3rem)] md:w-56 md:self-start md:overflow-y-auto">
      <button
        ref={toggleRef}
        type="button"
        aria-controls={navigationId}
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left font-medium focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-400 md:hidden"
      >
        <Menu className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1">Navigation · {activeTab}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      <nav
        id={navigationId}
        aria-label="Administration"
        className={`${expanded ? "block" : "hidden"} pt-2 md:block md:pt-0`}
      >
        <ul className="space-y-1">
          {tabs.map((tabName) => (
            <li key={tabName}>
              <button
                type="button"
                aria-label={tabName}
                aria-current={tabName === activeTab ? "page" : undefined}
                onClick={() => {
                  onSelect(tabName);
                  setExpanded(false);
                  // Return focus to the mobile toggle before its menu is hidden.
                  if (toggleRef.current?.getClientRects().length) {
                    toggleRef.current.focus();
                  }
                }}
                className={[
                  "flex min-h-11 w-full items-center rounded-lg border px-3 py-2.5 text-left text-sm font-medium transition-colors",
                  "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400",
                  tabName === activeTab
                    ? "border-sky-400 bg-slate-950 text-white shadow-xs"
                    : "border-transparent text-slate-300 hover:border-slate-600 hover:bg-slate-800 hover:text-white",
                ].join(" ")}
              >
                <span>{tabName}</span>
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
}
